import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '@/components/feature/PortalLayout';
import MovieListItem from '@/components/feature/MovieListItem';
import { movies, genreOptions, regionOptions, yearOptions } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

interface CategoryPageProps {
  type: 'movie' | 'tv';
}

const SORT_OPTIONS = [
  { key: 'latest', label: '最新发布' },
  { key: 'heat', label: '热门程度' },
  { key: 'title', label: '名称排序' },
];

export default function CategoryPage({ type }: CategoryPageProps) {
  const isMovie = type === 'movie';
  const categoryName = isMovie ? '电影' : '电视剧';
  const heading = isMovie ? '最新电影' : '热门电视剧';

  usePageMeta({
    title: `${categoryName} - 诱人的女员工hd影视专区`,
    description: `诱人的女员工hd影视专区${categoryName}频道，汇聚最新${categoryName}作品，支持按类型、地区、年份筛选与排序，帮助你快速找到想看的${categoryName}。`,
    keywords: `诱人的女员工hd,${categoryName},影视专区,剧情介绍,演员信息`,
  });

  const [genre, setGenre] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('latest');
  const [visible, setVisible] = useState(6);
  const [filterOpen, setFilterOpen] = useState(false);

  const base = useMemo(() => movies.filter((m) => m.type === type), [type]);

  const filtered = useMemo(() => {
    const list = base.filter((m) => {
      const okGenre = genre === '全部' || m.genres.includes(genre);
      const okRegion = region === '全部' || m.region === region;
      const okYear = year === '全部' || String(m.year) === year;
      return okGenre && okRegion && okYear;
    });
    return list.sort((a, b) => {
      if (sort === 'heat') return b.heat - a.heat;
      if (sort === 'title') return a.title.localeCompare(b.title, 'zh');
      return b.releaseDate.localeCompare(a.releaseDate);
    });
  }, [base, genre, region, year, sort]);

  const resetFilters = () => {
    setGenre('全部');
    setRegion('全部');
    setYear('全部');
    setSort('latest');
    setVisible(6);
  };

  const onFilterChange = (setter: (v: string) => void, value: string) => {
    setter(value);
    setVisible(6);
  };

  const shown = filtered.slice(0, visible);
  const activeCount = [genre, region, year].filter((v) => v !== '全部').length;

  return (
    <PortalLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* breadcrumb */}
        <nav className="flex items-center gap-1 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-accent-600 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">{categoryName}</span>
        </nav>

        <h1 className="font-heading font-black text-foreground-950 text-2xl md:text-3xl">{heading}</h1>
        <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
          {isMovie
            ? '甄选院线与网络原创影片，涵盖爱情、动作、悬疑、科幻等多种类型，支持组合筛选，快速找到你想看的电影。'
            : '汇集口碑与热播剧集，覆盖都市、古装、悬疑、历史等题材，支持多条件筛选与排序，追剧更轻松。'}
        </p>

        {/* filter bar */}
        <div className="mt-6 bg-background-100 border border-background-200 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 md:hidden">
            <span className="text-sm font-medium text-foreground-800">
              筛选与排序{activeCount > 0 ? ` (${activeCount})` : ''}
            </span>
            <button
              type="button"
              onClick={() => setFilterOpen((v) => !v)}
              className="flex items-center gap-1 text-sm text-accent-600 cursor-pointer"
            >
              {filterOpen ? '收起' : '展开'}
              <i className={filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>
          </div>

          <div className={`${filterOpen ? 'block' : 'hidden'} md:block px-4 pb-4 md:py-4`}>
            <div className="flex flex-col gap-4">
              <FilterRow label="类型">
                {genreOptions.map((g) => (
                  <Chip key={g} active={genre === g} onClick={() => onFilterChange(setGenre, g)}>
                    {g}
                  </Chip>
                ))}
              </FilterRow>

              <FilterRow label="地区">
                {regionOptions.map((r) => (
                  <Chip key={r} active={region === r} onClick={() => onFilterChange(setRegion, r)}>
                    {r}
                  </Chip>
                ))}
              </FilterRow>

              <FilterRow label="年份">
                {yearOptions.map((y) => (
                  <Chip key={y} active={year === y} onClick={() => onFilterChange(setYear, y)}>
                    {y}
                  </Chip>
                ))}
              </FilterRow>

              <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-background-200 mt-1">
                <span className="text-sm text-foreground-500 pt-2">排序</span>
                <div className="flex items-center gap-1 px-1 py-1 rounded-full bg-background-200 mt-2">
                  {SORT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setSort(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sort === opt.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="ml-auto mt-2 flex items-center gap-1 text-xs text-foreground-500 hover:text-accent-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>重置筛选
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* result count */}
        <p className="mt-5 text-sm text-foreground-500">共找到 {filtered.length} 部作品</p>

        {/* list */}
        {shown.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {shown.map((m) => (
              <MovieListItem key={m.id} movie={m} />
            ))}
          </div>
        ) : (
          <div className="mt-6 py-16 text-center bg-background-100 border border-dashed border-background-300 rounded-lg">
            <span className="inline-flex w-14 h-14 md:w-16 md:h-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 mb-4">
              <i className="ri-emotion-unhappy-line text-2xl"></i>
            </span>
            <p className="font-heading font-bold text-foreground-900">没有找到匹配的作品</p>
            <p className="mt-1 text-sm text-foreground-500">试试更换筛选条件，或重置后再看看</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选条件
            </button>
          </div>
        )}

        {/* load more */}
        {visible < filtered.length && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setVisible((v) => v + 6)}
              className="px-6 py-3 rounded-md border border-background-300 bg-background-100 text-foreground-800 text-sm font-medium hover:border-accent-400 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              加载更多（剩余 {filtered.length - visible} 部）
            </button>
          </div>
        )}
      </div>
    </PortalLayout>
  );
}

function FilterRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-2">
      <span className="text-sm text-foreground-500 shrink-0 sm:w-12 sm:pt-1.5">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
        active
          ? 'bg-accent-500 text-background-50'
          : 'bg-background-200 text-foreground-600 hover:bg-background-300'
      }`}
    >
      {children}
    </button>
  );
}